/**
 * MIØ-Ergänzungen im seitlichen Warenkorb (snippets/mio-warenkorb-extras.liquid):
 *
 * 1. Rabattcode mit einem Klick einlösen (teil 'rabatt').
 *    Nutzt das Rabattfeld von Horizon im Warenkorb (snippets/cart-summary.liquid):
 *    Code eintragen, Formular absenden. Horizon rendert den Warenkorb danach neu,
 *    der Hinweis verschwindet, weil der Code angewendet ist. Fehlt das Feld,
 *    übernimmt Shopifys Rabatt-Link den Code und führt zurück zum Warenkorb.
 *
 * 2. Vorschläge bis zum Gratisversand mit einem Tipp dazulegen (teil 'vorschlaege').
 *    Gleicher Weg wie Horizons Kaufknopf (assets/product-form.js): Ereignis
 *    CartLinesUpdateEvent mit Zusage, /cart/add.js mit den Warenkorb-Sektionen,
 *    danach rendert der Warenkorb sich neu, Zähler im Kopf und Warenkorb-Leiste
 *    ziehen mit.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

document.addEventListener('click', (e) => {
  const knopf = e.target.closest('[data-mio-rabatt]');
  if (!knopf) return;
  const code = knopf.dataset.mioRabatt;
  const bereich = knopf.closest('cart-drawer-component, .cart-drawer__inner') ?? document;
  const feld = bereich.querySelector('.cart-discount__input');
  const formular = feld?.closest('form');

  if (!feld || !formular?.requestSubmit) {
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(window.location.pathname)}`;
    return;
  }

  knopf.disabled = true;
  knopf.setAttribute('aria-busy', 'true');
  // Scheitert das Einlösen, rendert Horizon nicht neu: Knopf wieder freigeben.
  window.setTimeout(() => {
    knopf.disabled = false;
    knopf.removeAttribute('aria-busy');
  }, 6000);
  // Horizon zeigt Fehler im Rabattfeld; es liegt in einem zugeklappten <details>.
  formular.closest('details')?.setAttribute('open', '');
  feld.value = code;
  feld.dispatchEvent(new Event('input', { bubbles: true }));
  formular.requestSubmit();
});

/** Ein Vorschlag nach dem anderen: Zwei gleichzeitige Antworten würden sich beim Neuzeichnen überholen. */
let vorschlagLaeuft = false;

/**
 * Nach dem Neuzeichnen ist der getippte Knopf oft weg (Gratisversand erreicht
 * oder Vorschlag verbraucht). Dann den Fokus im Warenkorb halten statt ihn
 * auf <body> fallen zu lassen.
 * @param {Element} knopf
 */
function fokusHalten(knopf) {
  if (knopf.isConnected) return;
  const schublade = document.getElementById('cart-drawer');
  if (!schublade) return;
  const ziel =
    schublade.querySelector('[data-mio-vorschlag]') ??
    schublade.querySelector('.mio-wk-versand__text') ??
    schublade.querySelector('.theme-drawer__close-button');
  if (!ziel) return;
  if (!ziel.matches('button, a[href]')) ziel.setAttribute('tabindex', '-1');
  ziel.focus({ preventScroll: true });
}

document.addEventListener('click', async (e) => {
  const knopf = e.target.closest('[data-mio-vorschlag]');
  if (!knopf || vorschlagLaeuft) return;
  vorschlagLaeuft = true;

  const variante = Number(knopf.dataset.mioVorschlag);
  const produktSeite = knopf.closest('.mio-wk-vorschlag')?.querySelector('a[href]')?.href;
  const sektionen = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);

  // aria-busy statt disabled: Der Fokus bleibt auf dem Knopf.
  knopf.setAttribute('aria-busy', 'true');
  const zusage = CartLinesUpdateEvent.createPromise();
  knopf.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: variante, quantity: 1 }],
      promise: zusage.promise,
    })
  );

  let ergebnis;
  try {
    const antwort = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variante, quantity: 1 }], sections: sektionen.join(',') }),
    });
    ergebnis = await antwort.json();
    if (!antwort.ok || ergebnis.status) throw new Error(ergebnis.description || ergebnis.message || `HTTP ${antwort.status}`);
  } catch (fehler) {
    zusage.reject(fehler);
    knopf.removeAttribute('aria-busy');
    vorschlagLaeuft = false;
    console.warn('[mio-warenkorb] Vorschlag nicht hinzugefügt:', fehler);
    // Lieber zur Produktseite als ein stummer Fehlschlag
    if (produktSeite) window.location.href = produktSeite;
    return;
  }

  // Ab hier liegt die Sorte im Warenkorb. Scheitert nur das Nachladen, wie
  // bei Horizon (product-form.js) abbrechen, aber nicht wegleiten.
  try {
    const warenkorb = await (await fetch(`${Theme.routes.cart_url}.json`)).json();
    zusage.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(warenkorb),
      detail: {
        items: warenkorb.items,
        source: 'mio-warenkorb-vorschlag',
        itemCount: 1,
        sections: ergebnis.sections,
        didError: false,
      },
    });
    await zusage.promise;
    await new Promise((fertig) => requestAnimationFrame(fertig));
    fokusHalten(knopf);
  } catch (fehler) {
    zusage.reject(fehler);
    console.warn('[mio-warenkorb] Warenkorb nicht nachgeladen:', fehler);
  } finally {
    knopf.removeAttribute('aria-busy');
    vorschlagLaeuft = false;
  }
});
